// components/feature/MarketingSlides.jsx
// ─────────────────────────────────────────────────────────────
// Transform-based slider — no overflow-x-auto, no scrollIntoView.
// Uses CSS translateX so it never bleeds into the parent scroll area.
//
// THEMING NOTE: the slide gradients themselves (THEME_STYLES) are
// intentionally NOT mapped onto light/dark tokens. These are
// marketing/promo banners — content the backend controls, meant to
// read the same vivid way regardless of the user's app theme, much
// like a billboard doesn't change color because the room it's in
// is dark. Tying them to --color-success/--color-info/etc. would
// mute their intended vibrancy and couple promo content to UI chrome
// it shouldn't depend on. Only the surrounding CHROME (loading
// skeleton, pagination dots) is themed below.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const THEME_STYLES = {
  emerald: { bg: "linear-gradient(135deg, #065F46 0%, #059669 100%)", pill: "rgba(255,255,255,0.2)" },
  blue:    { bg: "linear-gradient(135deg, #1E3A5F 0%, #0284C7 100%)", pill: "rgba(255,255,255,0.2)" },
  purple:  { bg: "linear-gradient(135deg, #3B0764 0%, #7C3AED 100%)", pill: "rgba(255,255,255,0.2)" },
  orange:  { bg: "linear-gradient(135deg, #7C2D12 0%, #EA580C 100%)", pill: "rgba(255,255,255,0.2)" },
  rose:    { bg: "linear-gradient(135deg, #881337 0%, #E11D48 100%)", pill: "rgba(255,255,255,0.2)" },
  amber:   { bg: "linear-gradient(135deg, #78350F 0%, #D97706 100%)", pill: "rgba(255,255,255,0.2)" },
};

const API_BASE = import.meta.env.VITE_API_URL || "";

export default function MarketingSlides({ page = "home", compact = false }) {
  const navigate       = useNavigate();
  const [slides, setSlides]   = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  // Touch/drag state
  const dragStart   = useRef(null);
  const isDragging  = useRef(false);

  useEffect(() => {
    axios.get(`${API_BASE}/api/mtrade/marketing/slides?page=${page}`)
      .then(r => { if (r.data.status) setSlides(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  // Auto-advance
  const startInterval = useCallback(() => {
    clearInterval(intervalRef.current);
    if (slides.length < 2) return;
    intervalRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % slides.length);
    }, 4000);
  }, [slides.length]);

  useEffect(() => {
    startInterval();
    return () => clearInterval(intervalRef.current);
  }, [startInterval]);

  const goTo = (idx) => {
    setCurrent(idx);
    startInterval(); // reset timer on manual nav
  };

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    dragStart.current = e.touches[0].clientX;
    isDragging.current = true;
  };
  const handleTouchEnd = (e) => {
    if (!isDragging.current || dragStart.current === null) return;
    const delta = dragStart.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) {
      if (delta > 0) goTo((current + 1) % slides.length);
      else           goTo((current - 1 + slides.length) % slides.length);
    }
    isDragging.current = false;
    dragStart.current  = null;
  };

  if (loading) return (
    <div className={`w-full rounded-2xl bg-surface-hover animate-pulse ${compact ? "h-[90px]" : "h-[120px]"}`} />
  );
  if (slides.length === 0) return null;

  const height = compact ? "h-[90px]" : "h-[120px]";

  return (
    <div className="w-full">
      {/* Outer: clips the sliding track */}
      <div
        className={`relative w-full rounded-2xl overflow-hidden ${height}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Track: all slides side-by-side, moved with translateX */}
        <div
          className="flex h-full"
          style={{
            width:     `${slides.length * 100}%`,
            transform: `translateX(-${(current * 100) / slides.length}%)`,
            transition: "transform 400ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {slides.map(slide => {
            const theme = THEME_STYLES[slide.theme] || THEME_STYLES.emerald;
            return (
              <div
                key={slide._id}
                className="relative h-full flex-shrink-0 cursor-pointer active:opacity-90"
                style={{ width: `${100 / slides.length}%`, background: theme.bg }}
                onClick={() => slide.ctaLink && navigate(slide.ctaLink)}
              >
                {/* Decorative glow */}
                <div
                  className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10 pointer-events-none"
                  style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)", transform: "translate(35%, -35%)" }}
                />
                <div className="absolute bottom-0 left-0 w-20 h-20 rounded-full opacity-10 pointer-events-none"
                  style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)", transform: "translate(-35%, 35%)" }}
                />

                <div className={`relative z-10 h-full flex items-center gap-4 px-5 ${compact ? "py-3" : "py-4"}`}>
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(255,255,255,0.15)" }}>
                    <i className={`${slide.icon || "ri-gift-line"} text-2xl text-white`} />
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm leading-snug line-clamp-2 font-display">
                      {slide.title}
                    </p>
                    {slide.subtitle && !compact && (
                      <p className="text-white/70 text-xs mt-0.5 line-clamp-1">{slide.subtitle}</p>
                    )}
                    {slide.ctaText && (
                      <span
                        className="inline-block mt-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full text-white"
                        style={{ background: theme.pill }}>
                        {slide.ctaText} →
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots — only shown when more than 1 slide */}
      {slides.length > 1 && (
        <div className="flex justify-center items-center gap-1.5 mt-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`rounded-full transition-all duration-300 ${
                i === current ? "w-5 h-1.5 bg-brand" : "w-1.5 h-1.5 bg-border-strong"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
